import { z } from 'zod';
import { OpportunityEnum, RiskIndicators, Roles } from './conversation-classification-metadata.schema';

// ── Contact-Specific Enums ────────────────────────────────────────────────────────────────

export const ContactTypeEnum = z.enum(Roles).describe('Primary classification of this contact in relation to the user');

export const ContactSeniority = [
    'INDIVIDUAL_CONTRIBUTOR', // Entry to senior individual contributor
    'TEAM_LEAD', // Team lead, principal, senior roles
    'MANAGER', // People manager, director level
    'EXECUTIVE', // VP, SVP, C-level
    'FOUNDER_OWNER', // Founder, owner, board member
    'UNKNOWN',
] as const;
export const ContactSeniorityEnum = z.enum(ContactSeniority).describe('Seniority level of the contact');

export const ContactInfluence = [
    'HIGH', // Key decision maker or strong influencer
    'MEDIUM', // Has some influence on decisions
    'LOW', // Limited influence
    'UNKNOWN',
] as const;
export const ContactInfluenceEnum = z
    .enum(ContactInfluence)
    .describe('Level of influence this contact has in decision making');

export const RelationshipStrength = [
    'STRONG', // Close working relationship, frequent interaction
    'MODERATE', // Some shared history, occasional interaction
    'WEAK', // Minimal shared context, rare interaction
    'NONE', // No prior relationship
    'UNKNOWN',
] as const;
export const RelationshipStrengthEnum = z
    .enum(RelationshipStrength)
    .describe('Strength of the relationship based on interaction history');

export const InitiationType = [
    'OUTBOUND', // User reached out first
    'INBOUND', // Contact reached out first
    'MUTUAL', // Both reached out around same time
    'UNKNOWN',
] as const;
export const InitiationTypeEnum = z
    .enum(InitiationType)
    .describe('Who initiated the relationship - inbound or outbound');

export const BusinessValue = [
    'VERY_HIGH', // Major enterprise client potential, high revenue
    'HIGH', // Significant business value
    'MEDIUM', // Moderate business value
    'LOW', // Limited business value
    'NONE', // No business value
    'UNKNOWN',
] as const;
export const BusinessValueEnum = z.enum(BusinessValue).describe('Potential business value of this contact');

// Simplified and MECE timing urgency
export const TimingUrgency = [
    'IMMEDIATE', // Hot lead, needs immediate attention
    'SHORT_TERM', // Should be contacted within 1-2 weeks
    'MEDIUM_TERM', // Should be contacted within 1-2 months
    'LONG_TERM', // Future opportunity, 3+ months
    'UNCLEAR', // Timing not determined
] as const;
export const TimingUrgencyEnum = z.enum(TimingUrgency).describe('How urgently this contact should be prioritized');

export const RelationshipHealth = [
    'EXCELLENT', // Strong positive relationship
    'GOOD', // Healthy relationship
    'NEUTRAL', // No strong relationship either way
    'STRAINED', // Some issues or tensions
    'POOR', // Relationship problems
    'UNKNOWN',
] as const;
export const RelationshipHealthEnum = z.enum(RelationshipHealth).describe('Current health of the relationship');

export const EngagementLevel = [
    'VERY_HIGH', // Highly engaged, responsive
    'HIGH', // Good engagement
    'MEDIUM', // Moderate engagement
    'LOW', // Limited engagement
    'VERY_LOW', // Barely engaged
    'DORMANT', // No recent engagement
] as const;
export const EngagementLevelEnum = z.enum(EngagementLevel).describe("Contact's engagement level");

// Consolidated and MECE contact actions
export const ContactAction = [
    // Direct Communication
    'SEND_MESSAGE', // LinkedIn message, email, or other direct message
    'SEND_CONNECTION_REQUEST', // LinkedIn connection request

    // Meeting & Engagement
    'SCHEDULE_MEETING', // Call, meeting, or demo
    'SEND_INFORMATION', // Proposal, case study, pricing, or content

    // Relationship Building
    'MAKE_INTRODUCTION', // Introduce to others or request introduction
    'REFERENCE_SHARED_CONTEXT', // Leverage shared experience or connection

    // Follow-up & Nurturing
    'FOLLOW_UP', // Follow up on previous conversation
    'NURTURE_RELATIONSHIP', // General relationship maintenance

    // Internal & Strategic
    'RESEARCH_MORE', // Gather more information before acting
    'ESCALATE_INTERNALLY', // Involve others from your team

    // Wait States
    'WAIT_FOR_RESPONSE', // Wait for their response
    'NONE', // No action needed
] as const;
export const ContactActionEnum = z.enum(ContactAction).describe('Primary recommended action to take');

export const ContactPriority = [
    'CRITICAL', // Immediate attention required
    'HIGH', // High priority
    'MEDIUM', // Medium priority
    'LOW', // Low priority
    'BACKLOG', // For future consideration
] as const;
export const ContactPriorityEnum = z.enum(ContactPriority).describe('Overall priority level for this contact');

export const CompanyStage = ['STARTUP', 'GROWTH', 'SCALE_UP', 'MATURE', 'ENTERPRISE', 'PUBLIC', 'UNKNOWN'] as const;
export const CompanyStageEnum = z.enum(CompanyStage).describe("Stage of the contact's company");

export const CompanyFunding = [
    'PRE_SEED',
    'SEED',
    'SERIES_A',
    'SERIES_B',
    'SERIES_C',
    'SERIES_D_PLUS',
    'IPO',
    'ACQUIRED',
    'BOOTSTRAPPED',
    'UNKNOWN',
] as const;
export const CompanyFundingEnum = z.enum(CompanyFunding).describe("Funding stage of the contact's company");

export const RiskFactorEnum = z.enum(RiskIndicators).describe('Risk factors that could impact success');

// Simplified and MECE shared context types
export const SharedContextType = [
    'WORK_HISTORY', // Worked at same company (current or past)
    'EDUCATION', // Same school, program, or alumni network
    'INDUSTRY_ROLE', // Same industry or similar job functions
    'GEOGRAPHIC', // Same location or region
    'NETWORK_CONNECTIONS', // Mutual LinkedIn connections or professional network
    'BUSINESS_RELATIONSHIP', // Existing customer/vendor/partner relationship
] as const;
export const SharedContextTypeEnum = z
    .enum(SharedContextType)
    .describe('Types of shared context between user and contact');

export const ObjectiveKey = [
    'networking',
    'sales',
    'customerSuccess',
    'fundraising',
    'recruiting',
    'internal',
] as const;
export const ObjectiveKeyEnum = z.enum(ObjectiveKey).describe('Key objective for the contact');

export const ContactClassificationMetadataComputedFieldsSchema = z.object({
    linkedinConnectedAt: z.string().nullable().describe('ISO date when they connected on LinkedIn'),
    companySize: z.number().nullable().describe('Estimated number of employees'),
    totalInteractions: z.number().describe('Total number of interactions with this contact'),
    lastEngagementDate: z.string().nullable().describe('ISO date of last meaningful engagement'),
    lastMessageDate: z.string().nullable().describe('ISO date of last message sent to the contact'),
    isConnected: z.boolean().nullable().describe('Whether the contact is connected to the user'),
    // sharedConnections: z.number().describe('Number of shared LinkedIn connections'),
    // initiationType: InitiationTypeEnum.describe('Who initiated the relationship - inbound or outbound'),
    recentNews: z.string().nullable().describe('Recent news about the contact or their company'),
    opportunities: z.array(OpportunityEnum).describe('Positive growth or engagement opportunities identified'), // From Conversation Classification Metadata
    sharedContextTypes: z.array(SharedContextTypeEnum).describe('Types of shared context between user and contact'),
    sharedCompanies: z.array(z.string()).describe('Names of companies both user and contact worked at'),
    sharedSchools: z.array(z.string()).describe('Names of schools both user and contact attended'),
    sharedTimelineEvents: z
        .array(
            z.object({
                timeframe: z.string().describe('When this overlap occurred (e.g., "2018-2020")'),
                context: z.string().describe('What the overlap was (e.g., "Both worked at Microsoft")'),
                significance: z.enum(['HIGH', 'MEDIUM', 'LOW']).describe('How significant this overlap is'),
            }),
        )
        .describe('Timeline of shared experiences and overlaps'),
});

export const ContactClassificationMetadataGeneratedFieldsSchema = z.object({
    // ── Contact Profile ────────────────────────────────────────────────────────────────
    contactType: ContactTypeEnum.describe('Primary role-based classification of this contact'), // @TODO: Look at adding score per enum
    seniority: ContactSeniorityEnum.describe('Seniority level of the contact within their organization'),
    influence: ContactInfluenceEnum.describe(
        'Level of influence this contact has in decision making within their organization',
    ),

    // ── Relationship Context & History ────────────────────────────────────────────────────────────────
    relationshipStrength: RelationshipStrengthEnum.describe(
        'Strength of the relationship between user and contact based on interaction history and mutual familiarity',
    ),

    // ── Shared Context & Overlap ────────────────────────────────────────────────────────────────
    relationshipOriginStory: z
        .string()
        .describe('Narrative of how and when the relationship between user and contact began'),
    // ── Company Context ────────────────────────────────────────────────────────────────
    companyStage: CompanyStageEnum.describe("Stage of the contact's company (startup to enterprise)"),
    // isTargetAccount: z
    //     .boolean()
    //     .describe("Whether this contact's company is a potential target account to the user's business"),

    // ── Business Value & Potential ────────────────────────────────────────────────────────────────
    businessValue: BusinessValueEnum.describe(
        "Potential business value this contact could provide to the user or user's business",
    ),
    strategicImportance: z
        .enum(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE', 'UNKNOWN'])
        .describe(
            "Strategic importance to user's long-term goals beyond immediate revenue (partnerships, market access, industry insights)",
        ),
    personalImportance: z
        .enum(['HIGH', 'MEDIUM', 'LOW', 'NONE', 'UNKNOWN'])
        .describe(
            'Personal importance to the user beyond business value (e.g. mentorship, friendship, career development)',
        ),

    // ── Relationship & Engagement ────────────────────────────────────────────────────────────────
    relationshipHealth: RelationshipHealthEnum.describe(
        'Current health and tone of the relationship between user and contact',
    ),
    engagementLevel: EngagementLevelEnum.describe(
        "Contact's level of engagement and responsiveness in communications from both the contact and the user",
    ),

    // ── Interest & Intent Signals ────────────────────────────────────────────────────────────────
    buyingIntent: z
        .enum(['VERY_HIGH', 'HIGH', 'MEDIUM', 'LOW', 'NONE', 'UNKNOWN'])
        .describe("Likelihood of contact making a purchase from the user's business or services"),
    decisionTimeframe: z
        .enum(['IMMEDIATE', 'THIS_QUARTER', 'NEXT_QUARTER', 'THIS_YEAR', 'UNCLEAR', 'NONE', 'UNKNOWN'])
        .describe('Expected timeframe for contact to make business decisions relevant to the user'),

    // ── Timing & Urgency ────────────────────────────────────────────────────────────────
    timingUrgency: TimingUrgencyEnum.describe(
        'How urgently this contact should be prioritized for outreach or follow-up',
    ),
    hasUpcomingEvent: z
        .boolean()
        .describe(
            "Whether there's an upcoming event or deadline relevant to this relationship or business opportunity",
        ),
    eventDescription: z
        .string()
        .nullable()
        .describe(
            'Description of upcoming event or deadline if any (funding rounds, product launches, job changes, etc.)',
        ),

    // ── Risk Assessment ────────────────────────────────────────────────────────────────
    riskFactors: z
        .array(RiskFactorEnum)
        .describe('Risk factors that could negatively impact the success of this relationship or business outcomes'),

    // ── Action & Priority ────────────────────────────────────────────────────────────────
    priority: ContactPriorityEnum.describe(
        'Overall priority level for this contact based on combined business value, timing, and relationship factors',
    ),
    urgentFollowUp: z
        .boolean()
        .describe(
            'Whether this contact requires urgent follow-up due to time-sensitive opportunities or relationship maintenance',
        ),
    relationshipLeverageOpportunity: z
        .string()
        .nullable()
        .describe(
            'Specific ways to leverage shared history, context, or connections when reaching out to this contact',
        ),
    objectiveScores: z
        .object({
            networking: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe(
                    "The contact relevance score (from the user's perspective) for general networking and relationship building (0-100). Low scores (0-30): Contacts with no shared context, industry, or mutual connections who are unresponsive or irrelevant to professional goals. Medium scores (31-70): Contacts with some shared background (industry, location, alma mater) or mutual connections with moderate engagement. High scores (71-100): Contacts with strong shared professional history (former colleagues, alumni), active mutual connections, similar seniority levels, and high engagement who can provide valuable introductions, advice, or collaboration opportunities.",
                ),
            sales: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe(
                    "The contact relevance score (from the user's perspective) for sales and business development (0-100). Low scores (0-30): Contacts outside target market, wrong company size/stage, no budget authority, or showing no interest/need. Medium scores (31-70): Contacts at target companies with some decision influence, moderate interest signals, or in evaluation phase but with barriers. High scores (71-100): Decision makers or strong influencers at ideal target accounts showing clear buying intent, budget authority, urgent need, and active engagement with sales process.",
                ),
            customerSuccess: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe(
                    "The contact relevance score (from the user's perspective) for customer success and retention (0-100). Low scores (0-30): Non-customers or customers with low product usage, no engagement, or satisfaction concerns. Medium scores (31-70): Existing customers with moderate usage, occasional support needs, or mixed feedback. High scores (71-100): Active customers showing high product adoption, expansion opportunities, strong satisfaction, advocacy potential, or critical issues needing immediate attention to prevent churn.",
                ),
            fundraising: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe(
                    "The contact relevance score (from the user's perspective) for fundraising and investment activities (0-100). Low scores (0-30): Contacts with no investment background, wrong investment stage/sector focus, or no interest in startup/growth investing. Medium scores (31-70): Angel investors, fund associates, or industry connections with some relevant investment activity. High scores (71-100): Active VCs/investors with portfolio fit, decision-making authority, current fundraising activity, strong referral potential to other investors, or demonstrated interest in the business sector/stage.",
                ),
            recruiting: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe(
                    "The contact relevance score (from the user's perspective) for talent acquisition and recruiting (0-100). Low scores (0-30): Contacts not actively job seeking, wrong skill set/experience level, or different industry focus. Medium scores (31-70): Contacts with relevant skills who might be open to opportunities or could provide referrals. High scores (71-100): High-quality candidates actively seeking roles, perfect skill/experience match, cultural fit indicators, strong referral sources in target roles, or talent who could significantly impact business growth.",
                ),
            internal: z
                .number()
                .int()
                .min(0)
                .max(100)
                .describe(
                    "The contact relevance score (from the user's perspective) for internal stakeholder management and company building (0-100). Low scores (0-30): External contacts with no connection to company operations, team, or strategic initiatives. Medium scores (31-70): External partners, advisors, or service providers with occasional business interaction. High scores (71-100): Current/former team members, board members, key advisors, strategic partners, major customers, or industry contacts critical to company operations, strategic decisions, or organizational development.",
                ),
        })
        .describe(
            "0-100 relevance scores for how good of a fit this contact is for each high-level objective (from the user's perspective)",
        ),

    // ── Context & Intelligence ────────────────────────────────────────────────────────────────
    personalizedContext: z
        .string()
        .describe('Key personal or company context that should be referenced for tailored and relevant outreach'),

    // ── Communication Preferences ────────────────────────────────────────────────────────────────
    preferredChannel: z
        .enum(['LINKEDIN', 'EMAIL', 'PHONE', 'IN_PERSON', 'UNKNOWN'])
        .describe(
            "Contact's preferred communication channel based on their response patterns and explicit preferences",
        ),
    communicationStyle: z
        .enum(['FORMAL', 'CASUAL', 'TECHNICAL', 'EXECUTIVE', 'MIXED', 'PERSONAL'])
        .describe("Contact's preferred communication style and tone based on their messages and professional context"),
    responsePattern: z
        .enum(['IMMEDIATE', 'SAME_DAY', 'WEEKLY', 'SLOW', 'INCONSISTENT', 'NONE', 'UNKNOWN'])
        .describe("Contact's typical response timing pattern to the user's communications"),

    // ── Scoring & Confidence ────────────────────────────────────────────────────────────────
    overallScore: z
        .number()
        .min(0)
        .max(100)
        .describe(
            "Overall contact score (0-100) of the contact (from the user's perspective). This is the most important score that considers urgency, business value, and personal importance for the user. High scores (71-100): Contacts with high urgency, business value, and personal importance for the user. Medium scores (31-70): Contacts with moderate urgency, business value, and personal importance for the user. Low scores (0-30): Contacts where no communication has happened in a while and no clear business value or personal importance, as well as contacts that are trying to sell something or spamming to the user.",
        ),
    relationshipScore: z
        .number()
        .min(0)
        .max(100)
        .describe(
            "Relationship strength score (0-100) of the contact (from the user's perspective). This is the score that considers the strength of the relationship based on interaction history and the overall enthusiasm of the user to engage with the contact.",
        ),

    confidenceLevel: z
        .enum(['VERY_HIGH', 'HIGH', 'MEDIUM', 'LOW', 'VERY_LOW'])
        .describe(
            'Confidence level in the accuracy of this entire classification based on available data quality and communication history',
        ),

    // ── Summary & Next Steps ────────────────────────────────────────────────────────────────
    contactSummary: z
        .string()
        .describe('2-3 sentence summary of this contact, their role, and their potential value to the user'),
    relationshipSummary: z
        .string()
        .describe(
            '1-2 sentence summary of the relationship history, current status, and shared context between user and contact',
        ),
    expectedOutcome: z.string().describe('Expected outcome or goal from the next interaction with this contact'),
});

export const ContactClassificationMetadataSchema = ContactClassificationMetadataComputedFieldsSchema.merge(
    ContactClassificationMetadataGeneratedFieldsSchema,
);

export type ContactClassificationMetadata = z.infer<typeof ContactClassificationMetadataSchema>;
